import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  ICheckoutSessionRequest,
  IAddressRequest,
  IPaymentData,
  IShippingRateRequest, PaymentStatusEnum
} from '@/data/_autogen/IValveVista';

const STORAGE_KEY = 'valve_vista_checkout_session';

export const useCheckoutStore = defineStore('checkout', () => {
  // State
  const checkoutSession = ref<ICheckoutSessionRequest | null>(null);
  const currentStep = ref(1);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const paymentData = ref<IPaymentData | null>(null);

  // Getters
  const cartTotal = computed(() => {
    return (
      checkoutSession.value?.cartItems?.reduce(
        (sum, item) => sum + (item.unitPrice || 0) * (item.quantity || 0),
        0
      ) || 0
    );
  });

  const shippingCost = computed(() => {
    return checkoutSession.value?.shippingRate?.price || 0;
  });

  const taxAmount = computed(() => {
    return checkoutSession.value?.taxAmount || 0;
  });

  const finalTotal = computed(() => {
    return (
      cartTotal.value +
      shippingCost.value +
      taxAmount.value
    );
  });

  const paymentStatus = computed(() => {
    return checkoutSession.value?.paymentStatus || 0;
  });

  const isCheckoutValid = computed(() => {
    return (
      checkoutSession.value?.isValid ||
      (checkoutSession.value &&
        checkoutSession.value.shippingAddress &&
        checkoutSession.value.billingAddress &&
        checkoutSession.value.shippingRate)
    );
  });

  // Actions
  const setCheckoutSession = (session: ICheckoutSessionRequest | null) => {
    checkoutSession.value = session;
  };

  const updateSession = (data: Partial<ICheckoutSessionRequest>) => {
    if (checkoutSession.value) {
      checkoutSession.value = { ...checkoutSession.value, ...data };
      persistToLocalStorage();
    }
  };

  const setCurrentStep = (step: number) => {
    currentStep.value = step;
    persistToLocalStorage();
  };

  const setIsLoading = (loading: boolean) => {
    isLoading.value = loading;
  };

  const setError = (errorMsg: string | null) => {
    error.value = errorMsg;
  };

  const setPaymentData = (data: IPaymentData | null) => {
    paymentData.value = data;
    // Don't persist payment data (security)
  };

  const updateShippingAddress = (address: IAddressRequest) => {
    if (checkoutSession.value) {
      checkoutSession.value.shippingAddress = address;
      persistToLocalStorage();
    }
  };

  const updateBillingAddress = (address: IAddressRequest) => {
    if (checkoutSession.value) {
      checkoutSession.value.billingAddress = address;
      persistToLocalStorage();
    }
  };

  const updateShippingRate = (rate: IShippingRateRequest) => {
    if (checkoutSession.value) {
      checkoutSession.value.shippingRate = rate;
      persistToLocalStorage();
    }
  };

  const updatePaymentStatus = (status: PaymentStatusEnum) => {
    if(checkoutSession.value) {
      checkoutSession.value.paymentStatus = status;
      persistToLocalStorage();
    }
  };

  // Persistence
  const persistToLocalStorage = () => {
    if (!checkoutSession.value) return;

    const safeData = {
      id: checkoutSession.value.id,
      cartId: checkoutSession.value.cartId,
      paymentIntentId: checkoutSession.value.paymentIntentId,
      finalAmount: checkoutSession.value.finalAmount,
      taxAmount: checkoutSession.value.taxAmount,
      paymentStatus: checkoutSession.value.paymentStatus,
      message: checkoutSession.value.message,
      cartItems: checkoutSession.value.cartItems,
      shippingAddress: checkoutSession.value.shippingAddress,
      billingAddress: checkoutSession.value.billingAddress,
      shippingRate: checkoutSession.value.shippingRate,
      currentStep: currentStep.value,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeData));
    } catch (e) {
      console.error('Failed to persist checkout session:', e);
    }
  };

  const loadFromLocalStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        return data;
      }
    } catch (e) {
      console.error('Failed to load checkout session:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };

  const hydrate = () => {
    const stored = loadFromLocalStorage();
    if (stored) {
      checkoutSession.value = stored;
      currentStep.value = stored.currentStep || 1;
    }
  };

  const resetCheckout = () => {
    checkoutSession.value = null;
    currentStep.value = 1;
    error.value = null;
    paymentData.value = null;
    isLoading.value = false;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear checkout session:', e);
    }
  };

  return {
    // State
    checkoutSession,
    currentStep,
    isLoading,
    error,
    paymentData,

    // Getters
    cartTotal,
    shippingCost,
    taxAmount,
    finalTotal,
    isCheckoutValid,
    paymentStatus,

    // Actions
    setCheckoutSession,
    updateSession,
    setCurrentStep,
    setIsLoading,
    setError,
    setPaymentData,
    updateShippingAddress,
    updateBillingAddress,
    updateShippingRate,
    updatePaymentStatus,
    persistToLocalStorage,
    loadFromLocalStorage,
    hydrate,
    resetCheckout,
  };
});
